// @ts-nocheck
import { PropsWithChildren } from 'react';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/filled-tonal-button.js';
import '@material/web/button/elevated-button.js';
import '@material/web/button/text-button.js';
import { Icon } from '../icon/icon';
import { StyledDiv } from '../../common/components/styled-div';
import { Progress } from '../progress/progress';
import styled from '@emotion/styled';

const LoadingButtonWrapper = styled.div<{ width?: string }>`
  position: relative;
  width: ${(props) => props.width || 'auto'};
  display: inline-block;
  && {
    width: inherit;
  }
`;

export interface ButtonProps extends PropsWithChildren {
  variant?:
    | 'filled-verified'
    | 'filled'
    | 'outlined'
    | 'filled-tonal'
    | 'elevated'
    | 'text'
    | 'delete-filled'
    | 'delete-outlined'
    | 'deleted-text';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  icon?: string;
  trailingIcon?: boolean;
  onClick?: () => void;
  onControlledClick?: (event: React.MouseEvent<HTMLElement>) => void;
  iconSize?: number;
  iconColor?: string;
  width?: string;
  loading?: boolean;
}

export const Button = ({
  children,
  variant = 'filled',
  disabled = false,
  type = 'button',
  icon,
  trailingIcon,
  onClick,
  onControlledClick,
  iconSize = 'normal',
  iconColor = '--md-sys-color-on-primary',
  width = '',
  loading = false,
  ...otherProps
}: ButtonProps) => {
  const props = {};

  // if loading is true then also make the button disabled
  disabled = loading || disabled;

  if (disabled) {
    props.disabled = true;
  }
  if (onClick) {
    props.onClick = onClick;
  }
  if (onControlledClick) {
    props.onClick = onControlledClick;
  }
  if (icon) {
    props.hasIcon = true;
    if (trailingIcon) {
      props['trailing-icon'] = true;
    }
  }
  if (iconColor && disabled) {
    iconColor = '--md-sys-color-on-surface-variant';
  }

  const FilledDeleteStyle = {
    '--md-filled-button-container-color': 'var(--md-sys-color-schemes-error)',
    '--md-filled-button-label-text-color': 'var(--md-sys-color-on-error)',
    '--md-filled-button-hover-label-text-color': 'var(--md-sys-color-on-error)',
    '--md-filled-button-pressed-label-text-color':
      'var(--md-sys-color-on-error)',
    '--md-filled-button-focus-label-text-color': 'var(--md-sys-color-on-error)',
    '--md-filled-button-hover-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-filled-button-pressed-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-filled-button-focus-state-layer-color':
      'var(--md-sys-color-schemes-error)',
  };

  const OutlineDeleteStyle = {
    '--md-outlined-button-label-text-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-outline-color': 'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-pressed-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-hover-label-text-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-pressed-label-text-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-focus-icon-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-hover-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-outlined-button-focus-label-text-color':
      'var(--md-sys-color-schemes-error)',
  };

  const TextDeleteStyle = {
    '--md-text-button-label-text-color': 'var(--md-sys-color-schemes-error)',
    '--md-text-button-outline-color': 'var(--md-sys-color-schemes-error)',
    '--md-text-button-pressed-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-text-button-hover-label-text-color':
      'var(--md-sys-color-schemes-error)',
    '--md-text-button-pressed-label-text-color':
      'var(--md-sys-color-schemes-error)',
    '--md-text-button-focus-icon-color': 'var(--md-sys-color-schemes-error)',
    '--md-text-button-hover-state-layer-color':
      'var(--md-sys-color-schemes-error)',
    '--md-text-button-focus-label-text-color':
      'var(--md-sys-color-schemes-error)',
  };

  const MarkVerifiedStyle = {
    '--md-filled-button-container-color': 'var(--md-ref-secondary-60)',
  };
  if (width) {
    // will remove width props as there is no token for that.
    props.style = { width };
  }
  if (variant === 'deleted-text') {
    // will remove width props as there is no token for that.
    props.style = { width, ...TextDeleteStyle };
  }
  if (variant === 'filled-verified') {
    // will remove width props as there is no token for that.
    props.style = { width, ...MarkVerifiedStyle };
  }

  switch (variant) {
    case 'filled':
    case 'filled-verified':
      if (loading) {
        return (
          <LoadingButtonWrapper width={width}>
            <md-filled-button
              {...otherProps}
              {...props}
              style={{ ...props.style, width: '100%' }}
            >
              {icon && (
                <Icon
                  name={icon}
                  slot="icon"
                  size={iconSize}
                  color={iconColor}
                />
              )}
              {children}
            </md-filled-button>
            <StyledDiv
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              justifyContent="center"
              alignItem="center"
            >
              <Progress type='circular' indeterminate={true} size='medium'/>
            </StyledDiv>
          </LoadingButtonWrapper>
        );
      }
      return (
        <md-filled-button {...otherProps} {...props}>
          {icon && (
            <Icon name={icon} slot="icon" size={iconSize} color={iconColor} />
          )}
          {children}
        </md-filled-button>
      );
    case 'outlined':
      return (
        <md-outlined-button {...otherProps} {...props}>
          {children}
          {icon && (
            <Icon name={icon} slot="icon" size={iconSize} color={iconColor} />
          )}
        </md-outlined-button>
      );
    case 'delete-filled':
      return (
        <md-filled-button style={FilledDeleteStyle} {...otherProps} {...props}>
          {children}
        </md-filled-button>
      );
    case 'delete-outlined':
      return (
        <md-outlined-button
          style={OutlineDeleteStyle}
          {...otherProps}
          {...props}
        >
          {children}
        </md-outlined-button>
      );
    case 'filled-tonal':
      return (
        <md-filled-tonal-button {...otherProps} {...props}>
          {children}
        </md-filled-tonal-button>
      );
    case 'elevated':
      return (
        <md-elevated-button {...otherProps} {...props}>
          {children}
        </md-elevated-button>
      );
    case 'text':
      return (
        <md-text-button {...otherProps} {...props}>
          {children}
          {icon && (
            <Icon name={icon} slot="icon" size={iconSize} color={iconColor} />
          )}
        </md-text-button>
      );
    case 'deleted-text':
      return (
        <md-text-button {...otherProps} {...props}>
          {children}
          {icon && (
            <Icon name={icon} slot="icon" size={iconSize} color={iconColor} />
          )}
        </md-text-button>
      );
  }
};

export { StyledDiv };
